import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ResponsiveGrid from "../components/ResponsiveGrid";
import ERPAnalyticsHeader from "../components/ERPAnalyticsHeader";
import Topbar from "../components/Topbar";
import WidgetRenderer from "../components/WidgetRenderer";
import DataExplorer from "../components/DataExplorer";
import { api } from "../lib/api";
import { Plus, Save, Trash2, Copy, Settings as Cog, X } from "lucide-react";

const WIDGET_LIB = [
  { type: "kpi", label: "KPI Card", w: 3, h: 2 },
  { type: "bar", label: "Bar Chart", w: 6, h: 4 },
  { type: "line", label: "Line Chart", w: 6, h: 4 },
  { type: "pie", label: "Pie Chart", w: 4, h: 4 },
  { type: "donut", label: "Donut Chart", w: 4, h: 4 },
  { type: "table", label: "Data Table", w: 12, h: 5 },
  { type: "text", label: "Text / Heading", w: 6, h: 2 },
];

function uid() { return Math.random().toString(36).slice(2, 10); }

function isChartType(type) {
  return ["bar", "line", "pie", "donut"].includes(type);
}

function isNumberField(field) {
  return field?.role === "measure" || field?.data_type === "number" || field?.type === "number";
}

function isDateField(field) {
  const name = String(field?.name || "").toLowerCase();
  const type = String(field?.data_type || field?.type || "").toLowerCase();
  return type.includes("date") || name.includes("date") || name.includes("_dt") || name.endsWith("dt");
}

function pickDefaultWidgetFields(type, fields = []) {
  const usable = fields.filter((f) => f?.name);
  const measures = usable.filter(isNumberField);
  const dimensions = usable.filter((f) => !isNumberField(f));

  const firstMeasure = measures[0]?.name || usable[0]?.name || "";
  let firstDimension = "";

  if (type === "line") {
    firstDimension =
      usable.find(isDateField)?.name ||
      dimensions[0]?.name ||
      usable[0]?.name ||
      "";
  } else {
    firstDimension =
      dimensions[0]?.name ||
      usable.find((f) => !isNumberField(f))?.name ||
      usable[0]?.name ||
      "";
  }

  if (type === "kpi") {
    return {
      measure: firstMeasure,
      aggregation: firstMeasure ? "sum" : "count",
    };
  }

  if (type === "table") {
    return {
      selected_columns: usable.slice(0, 8).map((f) => f.name),
    };
  }

  if (isChartType(type)) {
    return {
      dimension: firstDimension,
      measure: firstMeasure,
      aggregation: firstMeasure ? "sum" : "count",
    };
  }

  return {};
}


export default function Builder() {
  const { id } = useParams(); // "new" or dashboardId
  const nav = useNavigate();
  const isNew = id === "new";

  const [name, setName] = useState("Untitled dashboard");
  const [description, setDescription] = useState("");
  const [widgets, setWidgets] = useState([]); // [{id, type, ...config}]
  const [layout, setLayout] = useState([]); // [{i,x,y,w,h}]
  const [filters, setFilters] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [datasetCols, setDatasetCols] = useState({}); // dataset_id -> [{name, role, data_type}]
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [rightTab, setRightTab] = useState("data"); // "data" | "config"

  useEffect(() => {
    api.get("/datasets").then((r) => {
      const merged = [...(r.data.datasets || []), ...(r.data.demo_datasets || [])];
      setDatasets(merged);
      const map = {};
      merged.forEach((d) => { map[d.id || `demo:${d.demo_key}`] = d.fields || []; });
      setDatasetCols(map);
    });
  }, []);

  useEffect(() => {
    if (isNew) return;
    api.get(`/dashboards/${id}`).then((r) => {
      const d = r.data;
      setName(d.name);
      setDescription(d.description || "");
      setWidgets(d.widgets || []);
      setLayout(d.layout || []);
      setFilters(d.filters || []);
    });
  }, [id, isNew]);

  const layouts = useMemo(() => ({ lg: layout.map((l) => ({ ...l })) }), [layout]);

  const addWidget = (type) => {
    const lib = WIDGET_LIB.find((w) => w.type === type) || { w: 4, h: 3 };
    const newId = `w_${uid()}`;

    const defaultDataset = datasets.find((d) => d?.id && !String(d.id).startsWith("demo:")) || datasets[0];
    const datasetId = defaultDataset ? (defaultDataset.id || `demo:${defaultDataset.demo_key}`) : "";
    const fields = datasetId ? (datasetCols[datasetId] || []) : [];
    const defaultFields = pickDefaultWidgetFields(type, fields);

    const w = {
      id: newId,
      type,
      title: `${type.toUpperCase()} widget`,
      dataset_id: type === "text" ? undefined : datasetId,
      aggregation: "sum",
      limit: type === "kpi" ? 1 : 100,
      filters: [],
      ...defaultFields,
    };

    if (type === "text") {
      w.text_content = "Heading or notes...";
      delete w.dataset_id;
      delete w.dimension;
      delete w.measure;
      delete w.selected_columns;
    }

    setWidgets((s) => [...s, w]);

    // Place at bottom
    const maxY = layout.reduce((m, l) => Math.max(m, l.y + l.h), 0);
    setLayout((s) => [...s, { i: newId, x: 0, y: maxY, w: lib.w, h: lib.h, minW: 2, minH: 2 }]);
    setSelectedId(newId);
    setRightTab("config");
  };

  const removeWidget = (wid) => {
    setWidgets((s) => s.filter((w) => w.id !== wid));
    setLayout((s) => s.filter((l) => l.i !== wid));
    if (selectedId === wid) setSelectedId(null);
  };

  const duplicateWidget = (wid) => {
    const w = widgets.find((x) => x.id === wid);
    const l = layout.find((x) => x.i === wid);
    if (!w || !l) return;
    const newId = `w_${uid()}`;
    setWidgets((s) => [...s, { ...w, id: newId, title: `${w.title} (copy)` }]);
    setLayout((s) => [...s, { ...l, i: newId, y: l.y + l.h }]);
    setSelectedId(newId);
  };

  const onLayoutChange = (l) => {
    setLayout(l.map((x) => ({ i: x.i, x: x.x, y: x.y, w: x.w, h: x.h, minW: x.minW || 2, minH: x.minH || 2 })));
  };

  const updateWidget = (wid, patch) => {
    setWidgets((current) =>
      current.map((widget) =>
        widget.id === wid ? { ...widget, ...patch } : widget
      )
    );
  };

  const updateWidgetLayout = (wid, patch) => {
    setLayout((current) =>
      current.map((item) =>
        item.i === wid
          ? {
              ...item,
              ...patch,
              w: patch.w !== undefined
                ? Math.max(2, Math.min(12, Number(patch.w) || 2))
                : item.w,
              h: patch.h !== undefined
                ? Math.max(2, Math.min(20, Number(patch.h) || 2))
                : item.h,
            }
          : item
      )
    );
  };

  const addDashboardFilter = () => {
    setFilters((current) => [
      ...current,
      {
        id: `f_${uid()}`,
        type: "dropdown",
        label: "New Filter",
        field: "",
      },
    ]);
  };

  const updateDashboardFilter = (filterId, patch) => {
    setFilters((current) =>
      current.map((filter) =>
        filter.id === filterId ? { ...filter, ...patch } : filter
      )
    );
  };

  const removeDashboardFilter = (filterId) => {
    setFilters((current) => current.filter((filter) => filter.id !== filterId));
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { name, description, widgets, layout, filters, tags: [] };
      const r = isNew ? await api.post("/dashboards", body) : await api.put(`/dashboards/${id}`, body);
      if (isNew) nav(`/builder/${r.data.id}`);
      else nav(`/dashboards/${id}`);
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const selected = widgets.find((w) => w.id === selectedId);
  const selectedLayout = layout.find((item) => item.i === selectedId);

  return (
    <div className="erp-home-shell">
      <ERPAnalyticsHeader />
      <main className="erp-content">
        <Topbar
          title={isNew ? "New dashboard" : `Edit · ${name}`}
          subtitle="Drag widgets from the palette. Configure data on the right."
          onRefresh={() => setRefreshKey((k) => k + 1)}
          right={(
            <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="builder-save">
              <Save size={14} strokeWidth={1.8} /> {saving ? "Saving…" : "Save"}
            </button>
          )}
        />
        <div className="builder-wide-workspace">
          {/* Compact builder toolbar */}
          <div className="builder-control-strip card-flat" data-testid="builder-palette">
            <div className="builder-control-section builder-widget-section">
              <div className="builder-section-title">Widgets</div>
              <div className="builder-widget-pills compact">
                {WIDGET_LIB.map((w) => (
                  <button
                    key={w.type}
                    onClick={() => addWidget(w.type)}
                    className="builder-widget-pill compact btn btn-outline"
                    data-testid={`add-${w.type}`}
                    title={`Add ${w.label}`}
                  >
                    <span>{w.label}</span>
                    <Plus size={12} strokeWidth={1.8} />
                  </button>
                ))}
              </div>
            </div>

            <div className="builder-control-section builder-details-section">
              <div className="builder-section-title">Dashboard Details</div>
              <div className="builder-inline-fields">
                <div>
                  <label className="label">Name</label>
                  <input
                    className="input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="builder-name"
                  />
                </div>

                <div>
                  <label className="label">Description</label>
                  <input
                    className="input"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    data-testid="builder-desc"
                    placeholder="Short dashboard purpose"
                  />
                </div>
              </div>
            </div>

            <div className="builder-control-section builder-filter-section">
              <div className="builder-filter-title-row">
                <div className="builder-section-title">Filters</div>
                <button
                  type="button"
                  className="btn btn-outline builder-small-action"
                  onClick={addDashboardFilter}
                  data-testid="add-dashboard-filter"
                >
                  <Plus size={12} strokeWidth={1.8} />
                  Add
                </button>
              </div>

              {filters.length === 0 ? (
                <div className="builder-empty-filter">No filters added.</div>
              ) : (
                <div className="builder-filter-list-compact">
                  {filters.map((filter) => (
                    <div
                      key={filter.id}
                      className="builder-filter-chip"
                      data-testid={`dashboard-filter-${filter.id}`}
                    >
                      <input
                        className="input"
                        placeholder="Label"
                        value={filter.label || ""}
                        onChange={(e) => updateDashboardFilter(filter.id, { label: e.target.value })}
                      />

                      <select
                        className="select"
                        value={filter.type || "dropdown"}
                        onChange={(e) => updateDashboardFilter(filter.id, { type: e.target.value })}
                      >
                        <option value="dropdown">Dropdown</option>
                        <option value="date_range">Date Range</option>
                      </select>

                      <input
                        className="input"
                        placeholder="Dataset field"
                        value={filter.field || ""}
                        onChange={(e) => updateDashboardFilter(filter.id, { field: e.target.value })}
                      />

                      <button
                        type="button"
                        className="btn btn-ghost builder-remove-filter"
                        title="Remove filter"
                        onClick={() => removeDashboardFilter(filter.id)}
                      >
                        <Trash2 size={12} strokeWidth={1.7} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Canvas */}
          <div className="builder-canvas-area">
            <div className="dash-canvas">
              {layout.length === 0 && (
                <div className="h-[600px] flex items-center justify-center text-[13px]" style={{ color: "var(--c-text-muted)" }} data-testid="builder-empty">
                  Click a widget on the left to add it here. Drag widgets to reposition, drag corners to resize.
                </div>
              )}
              <ResponsiveGrid
                className="layout"
                layouts={layouts}
                breakpoints={{ lg: 1200, md: 996, sm: 768 }}
                cols={{ lg: 12, md: 10, sm: 6 }}
                rowHeight={42}
                margin={[10, 10]}
                isDraggable
                isResizable
                draggableHandle=".wh"
                onLayoutChange={onLayoutChange}
              >
                {layout.map((l) => {
                  const w = widgets.find((x) => x.id === l.i);
                  if (!w) return <div key={l.i}/>;
                  const isSel = selectedId === w.id;
                  return (
                    <div key={l.i} data-testid={`builder-widget-${l.i}`}>
                      <div className="widget-shell" style={{ outline: isSel ? "2px solid var(--c-accent)" : "none" }}>
                        <div className="wh" onClickCapture={() => setSelectedId(w.id)}>
                          <div>
                            <div className="wt">{w.title || w.type}</div>
                            <div className="ws">{w.type}{w.dataset_id ? ` · ${w.dataset_id}` : ""}</div>
                          </div>
                          <div className="flex gap-1" onMouseDown={(e) => e.stopPropagation()}>
                            <button className="btn btn-ghost" onClick={() => setSelectedId(w.id)} title="Configure"><Cog size={13} strokeWidth={1.7} /></button>
                            <button className="btn btn-ghost" onClick={() => duplicateWidget(w.id)} title="Duplicate"><Copy size={13} strokeWidth={1.7} /></button>
                            <button className="btn btn-ghost" onClick={() => removeWidget(w.id)} title="Remove"><Trash2 size={13} strokeWidth={1.7} /></button>
                          </div>
                        </div>
                        <div className="wb"><WidgetRenderer widget={w} onRefreshKey={refreshKey} /></div>
                      </div>
                    </div>
                  );
                })}
              </ResponsiveGrid>
            </div>
          </div>

          {/* Config panel */}
          <div className="builder-side-panel card-flat" data-testid="builder-config">
            <div className="flex gap-1 mb-2">
              <button
                className={`text-[12px] px-2.5 py-1 rounded ${rightTab === "data" ? "bg-slate-900 text-white" : "bg-slate-100"}`}
                onClick={() => setRightTab("data")}
                data-testid="right-tab-data"
              >Data explorer</button>
              <button
                className={`text-[12px] px-2.5 py-1 rounded ${rightTab === "config" ? "bg-slate-900 text-white" : "bg-slate-100"} ${!selected ? "opacity-50" : ""}`}
                onClick={() => selected && setRightTab("config")}
                disabled={!selected}
                data-testid="right-tab-config"
              >Widget config</button>
            </div>
            <div className="flex-1 overflow-hidden">
              {rightTab === "config" && selected && (
                <div className="h-full overflow-auto">
                  <WidgetConfigPanel
                    widget={selected}
                    layoutItem={selectedLayout}
                    update={(p) => updateWidget(selected.id, p)}
                    updateLayout={(p) => updateWidgetLayout(selected.id, p)}
                    datasets={datasets}
                    datasetCols={datasetCols}
                    onClose={() => setSelectedId(null)}
                  />
                </div>
              )}
              {(rightTab === "data" || !selected) && (
                <DataExplorer
                  datasets={datasets}
                  activeDatasetId={selected?.dataset_id}
                  onPickDimension={(dsId, field) => {
                    if (!selected) return;
                    updateWidget(selected.id, { dataset_id: dsId, dimension: field.name });
                    setRightTab("config");
                  }}
                  onPickMeasure={(dsId, field) => {
                    if (!selected) return;
                    const isMeasure = field.role === "measure" || field.data_type === "number";
                    updateWidget(selected.id, {
                      dataset_id: dsId,
                      measure: field.name,
                      aggregation: isMeasure ? (selected.aggregation || "sum") : "count_distinct",
                    });
                    setRightTab("config");
                  }}
                  onCreateDatasetFromTable={(connId, t) => {
                    // Stash a hint and bounce to Datasets page where the New Dataset modal
                    // will pre-select the connection. Easier than embedding the full modal here.
                    sessionStorage.setItem("camaroo:new_dataset_hint", JSON.stringify({ connection_id: connId, base_table: t.name, schema: t.schema }));
                    nav("/datasets");
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function WidgetConfigPanel({
  widget,
  layoutItem,
  update,
  updateLayout,
  datasets,
  datasetCols,
  onClose,
}) {
  const cols = (datasetCols[widget.dataset_id] || []);
  const dims = cols.filter((c) => c.role === "dimension");
  const measures = cols.filter((c) => c.role === "measure");

  return (
    <div data-testid="config-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="kpi-label">Widget config</div>
        <button className="btn btn-ghost" onClick={onClose} data-testid="config-close"><X size={13} strokeWidth={1.8} /></button>
      </div>
      <div className="space-y-3">
        <div>
          <label className="label">Title</label>
          <input className="input" value={widget.title || ""} onChange={(e) => update({ title: e.target.value })} data-testid="cfg-title" />
        </div>

        <div className="border rounded p-3 space-y-2" style={{ borderColor: "var(--c-border)", background: "#fafbfc" }}>
          <div className="flex items-center justify-between">
            <label className="label mb-0">Widget size</label>
            <button
              type="button"
              className="btn btn-outline text-[11px]"
              onClick={() => updateLayout({ w: 12 })}
              disabled={!layoutItem}
              data-testid="cfg-full-width"
            >
              Full width
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label">Width</label>
              <select
                className="select"
                value={layoutItem?.w || 6}
                onChange={(e) => updateLayout({ w: Number(e.target.value) })}
                disabled={!layoutItem}
                data-testid="cfg-width"
              >
                <option value="2">2 / 12</option>
                <option value="3">3 / 12</option>
                <option value="4">4 / 12</option>
                <option value="5">5 / 12</option>
                <option value="6">6 / 12</option>
                <option value="7">7 / 12</option>
                <option value="8">8 / 12</option>
                <option value="9">9 / 12</option>
                <option value="10">10 / 12</option>
                <option value="11">11 / 12</option>
                <option value="12">12 / 12</option>
              </select>
            </div>

            <div>
              <label className="label">Height</label>
              <input
                className="input"
                type="number"
                min="2"
                max="20"
                value={layoutItem?.h || 3}
                onChange={(e) => updateLayout({ h: Number(e.target.value) })}
                disabled={!layoutItem}
                data-testid="cfg-height"
              />
            </div>
          </div>

          <div className="text-[11px]" style={{ color: "var(--c-text-muted)" }}>
            Width uses the 12-column dashboard grid. Height uses dashboard grid rows.
          </div>
        </div>
        {widget.type === "text" ? (
          <div>
            <label className="label">Text</label>
            <textarea className="input" rows="6" value={widget.text_content || ""} onChange={(e) => update({ text_content: e.target.value })} data-testid="cfg-text" />
          </div>
        ) : (
          <>
            <div>
              <label className="label">Dataset</label>
              <select
                className="select"
                value={widget.dataset_id || ""}
                onChange={(e) => update({
                  dataset_id: e.target.value,
                  dimension: undefined,
                  measure: undefined,
                  selected_columns: null
                })}
                data-testid="cfg-dataset"
              >
                <option value="">— select dataset —</option>
                {datasets.map((d) => (
                  <option key={d.id || `demo:${d.demo_key}`} value={d.id || `demo:${d.demo_key}`}>
                    {d.name}{d.is_demo ? " (demo)" : ""}
                  </option>
                ))}
              </select>
            </div>
            {widget.type === "table" ? (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="label mb-0">
                    Visible columns ({
                      widget.selected_columns === undefined ||
                      widget.selected_columns === null
                        ? cols.length
                        : widget.selected_columns.length
                    })
                  </label>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      className="btn btn-ghost text-[11px]"
                      onClick={() => update({ selected_columns: cols.map((c) => c.name) })}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost text-[11px]"
                      onClick={() => update({ selected_columns: [] })}
                    >
                      Clear
                    </button>
                  </div>
                </div>

                <div
                  className="border rounded p-2 max-h-[240px] overflow-auto text-[12.5px]"
                  style={{ borderColor: "var(--c-border)" }}
                  data-testid="cfg-table-columns"
                >
                  {!widget.dataset_id && (
                    <div style={{ color: "var(--c-text-muted)" }}>
                      Select a dataset first.
                    </div>
                  )}

                  {widget.dataset_id && cols.length === 0 && (
                    <div style={{ color: "var(--c-text-muted)" }}>
                      No fields available.
                    </div>
                  )}

                  {(() => {
                    const allColumnNames = cols.map((column) => column.name);

                    const selectedNames =
                      widget.selected_columns === undefined ||
                      widget.selected_columns === null
                        ? allColumnNames
                        : widget.selected_columns;

                    const selectedColumns = selectedNames
                      .map((name) => cols.find((column) => column.name === name))
                      .filter(Boolean);

                    const unselectedColumns = cols.filter(
                      (column) => !selectedNames.includes(column.name)
                    );

                    const moveColumn = (columnName, direction) => {
                      const current = [...selectedNames];
                      const currentIndex = current.indexOf(columnName);
                      const nextIndex = currentIndex + direction;

                      if (
                        currentIndex < 0 ||
                        nextIndex < 0 ||
                        nextIndex >= current.length
                      ) return;

                      [current[currentIndex], current[nextIndex]] = [
                        current[nextIndex],
                        current[currentIndex],
                      ];

                      update({ selected_columns: current });
                    };

                    return (
                      <>
                        {selectedColumns.length === 0 && (
                          <div
                            className="py-3 text-center"
                            style={{ color: "var(--c-text-muted)" }}
                          >
                            No columns selected. Use Select all or tick the
                            required fields below.
                          </div>
                        )}

                        {selectedColumns.map((column, index) => (
                          <div
                            key={`selected-${column.name}`}
                            className="flex items-center gap-2 py-1 px-1 rounded hover:bg-slate-50"
                          >
                            <input
                              type="checkbox"
                              checked
                              onChange={() =>
                                update({
                                  selected_columns: selectedNames.filter(
                                    (name) => name !== column.name
                                  ),
                                })
                              }
                            />

                            <span className="font-mono flex-1 truncate">
                              {column.display_name || column.name}
                            </span>

                            <span
                              className="text-[10px]"
                              style={{ color: "var(--c-text-muted)" }}
                            >
                              {column.data_type}
                            </span>

                            <button
                              type="button"
                              className="btn btn-ghost px-1.5"
                              disabled={index === 0}
                              onClick={() => moveColumn(column.name, -1)}
                              title="Move column up"
                            >
                              ↑
                            </button>

                            <button
                              type="button"
                              className="btn btn-ghost px-1.5"
                              disabled={index === selectedColumns.length - 1}
                              onClick={() => moveColumn(column.name, 1)}
                              title="Move column down"
                            >
                              ↓
                            </button>
                          </div>
                        ))}

                        {unselectedColumns.length > 0 && (
                          <div
                            className="mt-2 pt-2 border-t"
                            style={{ borderColor: "var(--c-border)" }}
                          >
                            <div
                              className="text-[10px] uppercase mb-1"
                              style={{ color: "var(--c-text-muted)" }}
                            >
                              Available columns
                            </div>

                            {unselectedColumns.map((column) => (
                              <label
                                key={`available-${column.name}`}
                                className="flex items-center gap-2 py-1 px-1 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={false}
                                  onChange={() =>
                                    update({
                                      selected_columns: [
                                        ...selectedNames,
                                        column.name,
                                      ],
                                    })
                                  }
                                />

                                <span className="font-mono flex-1 truncate">
                                  {column.display_name || column.name}
                                </span>

                                <span
                                  className="text-[10px]"
                                  style={{ color: "var(--c-text-muted)" }}
                                >
                                  {column.data_type}
                                </span>
                              </label>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            ) : (
              <>
                {widget.type !== "kpi" && (
                  <div>
                    <label className="label">Dimension (X-axis / group)</label>
                    <select className="select" value={widget.dimension || ""} onChange={(e) => update({ dimension: e.target.value })} data-testid="cfg-dim">
                      <option value="">— select —</option>
                      {dims.map((c) => <option key={c.name} value={c.name}>{c.display_name || c.name}</option>)}
                    </select>
                  </div>
                )}

                <div>
                  <label className="label">Measure</label>
                  <select className="select" value={widget.measure || ""} onChange={(e) => update({ measure: e.target.value })} data-testid="cfg-measure">
                    <option value="">— count rows —</option>
                    {measures.map((c) => <option key={c.name} value={c.name}>{c.display_name || c.name}</option>)}
                    {dims.map((c) => <option key={c.name + "_d"} value={c.name}>{c.display_name || c.name} (count distinct)</option>)}
                  </select>
                </div>

                <div>
                  <label className="label">Aggregation</label>
                  <select className="select" value={widget.aggregation || "sum"} onChange={(e) => update({ aggregation: e.target.value })} data-testid="cfg-agg">
                    {["sum", "avg", "count", "count_distinct", "min", "max"].map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
              </>
            )}
            {(widget.type === "bar" || widget.type === "line" || widget.type === "pie" || widget.type === "donut") && (
              <div>
                <label className="label">Limit</label>
                <input className="input" type="number" value={widget.limit || 100} onChange={(e) => update({ limit: Number(e.target.value) || 100 })} data-testid="cfg-limit" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label">Prefix</label>
                <input className="input" value={widget.number_format?.prefix || ""} onChange={(e) => update({ number_format: { ...(widget.number_format||{}), prefix: e.target.value } })} />
              </div>
              <div>
                <label className="label">Suffix</label>
                <input className="input" value={widget.number_format?.suffix || ""} onChange={(e) => update({ number_format: { ...(widget.number_format||{}), suffix: e.target.value } })} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
